import { ContextualBarAPI, DeckAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnMount, OnSelectItem, OnUnselectItem } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent, SelectItemEvent, SubmitEvent, UnselectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { UserDependent, UserPrincipal } from '../../app/types';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import UserContext from '../../utils/user-context';
import { Inject } from '@odinjs/odin';
import BaseController from '../BaseController';
import { Value } from '@philips-internal/oswald-ui-core';
import { foundationContext } from '@philips-internal/oswald-ui-foundation';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { ContextType } from '@philips-internal/oswald-ui-core';

interface AvatarProps {
  image: string;
  patientId: number;
  subtitle: string;
  title: string;
}

@Controller({fragment: 'change-user-modal'})
export default class ChangeUserModalController extends BaseController {

  @Inject('contextual-bar-dependents')
    contextualBarDependentsAPI!: ContextualBarAPI;

  @Inject('deck-principal')
    deckPrincipalAPI!: DeckAPI;

  @Inject('deck-dependents')
    deckDependentsAPI!: DeckAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('panel-deck-dependents')
    panelDeckDependentsAPI!: PanelAPI;

  @OnMount({target: 'deck-principal'})
  async handleLoadAvatarPrincipal(event: MountEvent): Promise<void> {
    const deckPrincipal = event.target as DeckAPI;

    let userPrincipal = UserContext.userPrincipal;
    if(userPrincipal == null) {
      const response = await HttpService.get<UserPrincipal>(APPLICATION_ENDPOINTS.GET_PHYSICAL_PERSON_DATA_WITH_PHOTO);
      userPrincipal = response.body;
      UserContext.userPrincipal = userPrincipal;
    }
    if(userPrincipal != null) {
      deckPrincipal.setData([{
        image: userPrincipal.imPessoaFisicaBase64,
        patientId: Number(userPrincipal.cdPessoaFisica || 0),
        subtitle: MessageService.translate('patient.account.holder'),
        title: userPrincipal.nmPessoaFisica,
      }]);
    }

    deckPrincipal.setDone(true);
  }

  @OnSelectItem({target: 'deck-principal'})
  handleSelectItemDeckPrincipal(event: SelectItemEvent<AvatarProps>): void {
    if(this.panelDeckDependentsAPI.isVisible()) {
      this.deckDependentsAPI.unselectAll();
    }
    this.switchUserOnSpotlight(event.item, true);
    this.submittableFragmentContext.submit(new SubmitEvent({record: {patientId: event.item.patientId}}));
  }

  @OnUnselectItem({target: 'deck-principal'})
  handleUnselectItemDeckPrincipal(event: UnselectItemEvent<AvatarProps>): void {
    const deckPrincipal = event.target as unknown as DeckAPI;
    deckPrincipal.selectCard(String(event.item.patientId));
  }

  @OnMount({target: 'deck-dependents'})
  async handleDeckDependentsLoading(event: MountEvent): Promise<void> {
    const deckDependents = event.target as DeckAPI;

    let dependents = UserContext.userDependents || [];
    if(dependents == null) {
      const response = await HttpService.get<UserDependent[]>(APPLICATION_ENDPOINTS.PROFILE_DEPENDENTS_LIST);
      dependents = response.body || [];
      UserContext.userDependents = dependents;
    }

    if(dependents.length === 0) {
      this.panelDeckDependentsAPI.setVisible(false);
      return;
    }

    const dependentsAvatar: AvatarProps[] = [];
    dependents.forEach(dependent => {
      dependentsAvatar.push({
        image: dependent.imPessoaFisicaBase64,
        patientId: Number(dependent.cdPessoaDependente || 0),
        subtitle: dependent.dsGrauParentesco,
        title: dependent.nmPessoaDependente,
      })
    });

    this.contextualBarDependentsAPI.setTitle(MessageService.translate('patient.header-dependents'));
    deckDependents.setData(dependentsAvatar as unknown as Record<string, Value>[]);
    deckDependents.setDone(true);
  }

  @OnSelectItem({target: 'deck-dependents'})
  handleSelectItemDeckDependents(event: SelectItemEvent<AvatarProps>): void {
    this.deckPrincipalAPI.unselectAll();
    this.switchUserOnSpotlight(event.item);
    this.submittableFragmentContext.submit(new SubmitEvent({record: {patientId: event.item.patientId}}));
  }

  @OnUnselectItem({target: 'deck-dependents'})
  handleUnselectItemDeckDependents(event: UnselectItemEvent<AvatarProps>): void {
    const deckDependents = event.target as unknown as DeckAPI;
    deckDependents.selectCard(String(event.item.patientId));
  }

  private switchUserOnSpotlight (avatarSelected: AvatarProps, isUserPrincipal?: boolean): void {
    const userInformation = foundationContext.getUser();
    if(userInformation) {
      const subtitle = isUserPrincipal ? 'patient.account.holder' : avatarSelected.subtitle;
      userInformation.id = String(avatarSelected.patientId || 0);
      userInformation.name = avatarSelected.title;
      userInformation.username = MessageService.translate(subtitle);
      userInformation.image = avatarSelected.image;
    }
  }
}